import React, { useRef, useState } from "react";
import { UploadCloud, FileText, X, Loader2, ScanLine, Youtube, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import SummaryView from "@/components/SummaryView";
import api, { formatApiError } from "@/lib/api";

const LENGTHS = ["short", "medium", "detailed"];
const IMG_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const MODES = [
  { id: "pdf", label: "Upload PDF", icon: UploadCloud },
  { id: "image", label: "Images", icon: ImageIcon },
  { id: "youtube", label: "YouTube", icon: Youtube },
  { id: "text", label: "Paste text", icon: ScanLine },
];

export default function Summarize() {
  const [mode, setMode] = useState("pdf");
  const [file, setFile] = useState(null);
  const [images, setImages] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [text, setText] = useState("");
  const [length, setLength] = useState("medium");
  const [subject, setSubject] = useState("General");
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const inputRef = useRef(null);
  const imageInputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File must be under 15 MB.");
      return;
    }
    setFile(f);
  };

  const addImages = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const valid = [];
    for (const f of incoming) {
      if (!IMG_TYPES.includes(f.type)) { toast.error(`${f.name}: use PNG, JPG or WEBP.`); continue; }
      if (f.size > 12 * 1024 * 1024) { toast.error(`${f.name} is larger than 12 MB.`); continue; }
      valid.push(f);
    }
    setImages((prev) => {
      const next = [...prev, ...valid].slice(0, 8);
      if (prev.length + valid.length > 8) toast.error("Up to 8 images at a time.");
      return next;
    });
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const generate = async () => {
    setLoading(true);
    setSummary(null);
    try {
      let data;
      if (mode === "pdf") {
        if (!file) { toast.error("Upload a PDF first."); setLoading(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("length", length);
        fd.append("subject", subject || "General");
        ({ data } = await api.post("/summaries/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else if (mode === "image") {
        if (!images.length) { toast.error("Add at least one image."); setLoading(false); return; }
        const fd = new FormData();
        images.forEach((img) => fd.append("files", img));
        fd.append("length", length);
        fd.append("subject", subject || "General");
        ({ data } = await api.post("/summaries/image", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else if (mode === "youtube") {
        if (!youtubeUrl.trim()) { toast.error("Paste a YouTube link first."); setLoading(false); return; }
        ({ data } = await api.post("/summaries/youtube", { url: youtubeUrl.trim(), length, subject: subject || "General" }));
      } else {
        if (text.trim().length < 40) { toast.error("Paste more text to summarize."); setLoading(false); return; }
        ({ data } = await api.post("/summaries/text", { text, length, subject: subject || "General" }));
      }
      setSummary(data.summary);
      toast.success("Summary ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate summary");
    } finally {
      setLoading(false);
    }
  };

  const busyLabel = mode === "youtube" ? "Reading the video…"
    : mode === "image" ? "Reading your pages…" : "Reading & summarizing…";

  return (
    <DashboardShell>
      <div className="mb-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">AI Summary</span>
        <h1 className="mt-3 font-sans text-3xl font-light tracking-tight md:text-4xl">
          Drop in anything. Let Quizer handle the hard part.
        </h1>
        <p className="mt-2 text-sm text-zinc-500">PDFs, photos of your notes, YouTube lessons, or pasted text — all become structured summaries and quizzes.</p>
      </div>

      {!summary && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${active ? "bg-white text-[#06080d]" : "glass text-zinc-300"}`}>
                  <Icon className="h-4 w-4" /> {m.label}
                </button>
              );
            })}
          </div>

          {mode === "pdf" && (
            file ? (
              <div className="glass flex items-center justify-between rounded-2xl p-5" data-testid="selected-file-card">
                <div className="flex items-center gap-4">
                  <FileText className="h-7 w-7 text-zinc-200" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium text-white">{file.name}</p>
                    <p className="font-mono text-xs text-zinc-500">{fmtSize(file.size)}</p>
                  </div>
                </div>
                <button onClick={() => setFile(null)} data-testid="remove-file-button" className="rounded-full p-2 text-zinc-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
                onClick={() => inputRef.current?.click()}
                data-testid="pdf-dropzone"
                className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-14 text-center transition-colors ${dragging ? "border-white/40 bg-white/[0.06]" : "border-white/15 bg-white/[0.02] hover:border-white/30"}`}
              >
                <UploadCloud className="h-10 w-10 text-zinc-300" strokeWidth={1.2} />
                <p className="mt-4 font-sans text-lg font-light">Drag & drop your PDF here</p>
                <p className="mt-1 text-sm text-zinc-500">or <span className="text-white underline-offset-4 hover:underline">Browse your device</span></p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">PDF only · up to 15 MB · 80 pages</p>
                <input ref={inputRef} type="file" accept="application/pdf" className="hidden" data-testid="pdf-file-input" onChange={(e) => pickFile(e.target.files?.[0])} />
              </div>
            )
          )}

          {mode === "image" && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); addImages(e.dataTransfer.files); }}
                onClick={() => imageInputRef.current?.click()}
                data-testid="image-dropzone"
                className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${dragging ? "border-white/40 bg-white/[0.06]" : "border-white/15 bg-white/[0.02] hover:border-white/30"}`}
              >
                <ImageIcon className="h-10 w-10 text-zinc-300" strokeWidth={1.2} />
                <p className="mt-4 font-sans text-lg font-light">Add photos of your textbook pages or notes</p>
                <p className="mt-1 text-sm text-zinc-500">Drag & drop or <span className="text-white underline-offset-4 hover:underline">browse</span></p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">PNG · JPG · WEBP · up to 8 images</p>
                <input ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" data-testid="image-file-input" onChange={(e) => addImages(e.target.files)} />
              </div>
              {images.length > 0 && (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5" data-testid="image-preview-grid">
                  {images.map((img, i) => (
                    <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-white/10">
                      <img src={URL.createObjectURL(img)} alt={img.name} className="h-full w-full object-cover" />
                      <button onClick={() => removeImage(i)} data-testid={`remove-image-${i}`}
                        className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === "youtube" && (
            <div className="space-y-3">
              <div className="glass flex items-center gap-3 rounded-2xl px-5 py-4">
                <Youtube className="h-6 w-6 shrink-0 text-zinc-300" strokeWidth={1.5} />
                <input
                  value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} data-testid="youtube-url-input"
                  placeholder="https://www.youtube.com/watch?v=…"
                  className="w-full bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
                />
              </div>
              <p className="text-xs text-zinc-500">We read the video's captions when available, otherwise we transcribe the audio with AI. Works best on videos under 40 minutes.</p>
            </div>
          )}

          {mode === "text" && (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="text-input"
              rows={9}
              placeholder="Paste a lesson, article, or question here…"
              className="w-full rounded-2xl border border-white/10 bg-white/5 p-5 text-sm leading-relaxed text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
            />
          )}

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Summary length</label>
              <div className="mt-2 flex gap-2">
                {LENGTHS.map((l) => (
                  <button key={l} onClick={() => setLength(l)} data-testid={`length-${l}`} className={`flex-1 rounded-xl border px-4 py-2.5 text-sm capitalize transition-colors ${length === l ? "border-white/50 bg-white/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Subject</label>
              <input
                value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="subject-input"
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
                placeholder="General"
              />
            </div>
          </div>

          <button onClick={generate} disabled={loading} data-testid="generate-summary-button" className="shimmer flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-[#06080d] transition-opacity disabled:opacity-50">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? busyLabel : "Generate Summary"}
          </button>
        </div>
      )}

      {summary && (
        <div className="space-y-6">
          <button onClick={() => { setSummary(null); setFile(null); setText(""); setImages([]); setYoutubeUrl(""); }} data-testid="new-summary-button" className="text-sm text-zinc-400 hover:text-white">
            ← Summarize another
          </button>
          <SummaryView summary={summary} />
        </div>
      )}
    </DashboardShell>
  );
}
